import { Component, Signal, signal, ChangeDetectionStrategy, inject, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalLayoutComponent } from '@program-suite/ui';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UI_FORM_IMPORTS } from '@program-suite/ui';
import { ButtonComponent } from '@program-suite/ui';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LookupViewModel } from '@program-suite/data-common';
import { LookupStore } from '@program-suite/data-common';
import { AuthStore } from '@program-suite/data-auth';
import { PolicyEndorsementsService } from '@program-suite/data-common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent, ToastType } from '@program-suite/ui';

@Component({
  selector: 'lib-void-endorsement',
  templateUrl: './void-endorsement.component.html',
  styleUrl: './void-endorsement.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalLayoutComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    ...UI_FORM_IMPORTS,
  ],
})
export class VoidEndorsementComponent {
  data: any = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<VoidEndorsementComponent>);
  form: FormGroup;
  policyNumber = this.data.policyNumber;
  title = signal('Void Endorsement');
  subTitle = signal('Please provide the reason for voiding this endorsement');

  constructor(
      private snackBar: MatSnackBar,
      private policyEndorsementsService: PolicyEndorsementsService,
  ) {
    this.form = new FormGroup({
      underwriterNotes: new FormControl('', [Validators.required]),
    });
    if (this.data?.policyChangeType) {
      this.title.set(`Void ${this.toSpacedTitleCase(this.data.policyChangeType)}`);
    }
    if (this.data?.subTitle) {
      this.subTitle.set(this.data.subTitle);
    }
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const confirmation = confirm('Are you sure you want to void this endorsement?');
    if (!confirmation) {
      return;
    }
    this.voidEndorsement();
  }
  voidEndorsement() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const formData = this.formatFormData();
    this.policyEndorsementsService.voidPolicyChangeRequest(formData).then((response: any) => {
      this.snackBar.openFromComponent(SnackbarComponent, {
        data: {
          message: 'Endorsement voided successfully',
          type: ToastType.Success,
        },
        duration: 5000,
      });
      this.dialogRef.close(response);
    }).catch((error: any) => {
      this.snackBar.openFromComponent(SnackbarComponent, {
        data: {
          message: 'Failed to void endorsement: ' + (error?.message || 'Unknown error'),
          type: ToastType.Error,
        },
        duration: 5000,
      });
      console.error('Error voiding endorsement:', error);
    });
  }

  formatFormData() {
    const formData = this.form.value;
    const formJson = {
      ...formData,
      underwriterNotes: `${this.data.endorsementNumber} - ${formData.underwriterNotes}`,
    }
    const form = new FormData();
    form.append('formJSON', JSON.stringify(formJson));
    form.append('voidPolicyChangeGuid', this.data.policyChangeGuid);
    return form;
  }

  private toSpacedTitleCase(text: string): string {
    return text.replace(/([A-Z])/g, ' $1').trim();
  }
}
